import { sanitizePublicHtml, safePublicUrl } from './publicContent'
/**
 * Portfolio page helpers.
 *
 * Expected API: getStudentPortfolioByApplication(application_id)
 * - members: from Member Contributions / team_credits (avatar + role)
 * - summary_video_url: Project Summary Video link (omit UI if empty)
 * - introduction / approach / highlights_impacts: AI summaries from Project Summary
 * - ideaboard_images: IdeaBoard deliverables (clickable)
 */

import { filterCertificateAwards } from './certificateAwards'
import { resolveProjectCycleLabel } from './applicationCycle'
import { buildQiniuPublicUrl, resolveDisplayMediaUrl } from './teamCredits'
import { resolveInnovationName } from './nimTrack'

export function pickAwardLabel(award) {
    if (!award) {
        return ''
    }
    if (typeof award === 'string') {
        return award.trim()
    }
    return award.award_lable || award.award_label || award.award_name || ''
}

function formatMemberRole(role) {
    if (!role) {
        return ''
    }
    if (role === '领队') {
        return 'Leader'
    }
    if (role === '队员') {
        return 'Member'
    }
    return role
}

export function memberInitials(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
    if (!parts.length) {
        return '?'
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase()
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

export function normalizeMembers(members) {
    if (!members || !members.length) {
        return []
    }
    return members.map((item, index) => {
        const name = item.name ||
            item.member ||
            [item.student_givenName_pinyin, item.student_lastName_pinyin].filter(Boolean).join(' ') ||
            item.student_name ||
            ('Member ' + (index + 1))
        return {
            name,
            avatarUrl: buildQiniuPublicUrl(item.avatar_url || item.avatar || item.student_avatar_url || ''),
            role: formatMemberRole(item.role || item.application_team_role || ''),
            initials: memberInitials(name)
        }
    }).filter(row => row.name)
}

export function normalizeIdeaBoardImages(images) {
    return (images || [])
        .filter(Boolean)
        .map(img => resolveDisplayMediaUrl(img))
        .filter(Boolean)
        .slice(0, 4)
}

function narrativeToHtml(value) {
    const text = String(value || '').trim()
    if (!text) {
        return ''
    }
    // Already HTML from older payloads
    if (/<\/?[a-z][\s\S]*>/i.test(text)) {
        return sanitizePublicHtml(text)
    }
    return text
        .split(/\n\s*\n/)
        .map(part => part.trim())
        .filter(Boolean)
        .map(part => '<p>' + escapeHtml(part) + '</p>')
        .join('')
}

function escapeHtml(value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
}

export function resolvePortfolioAwardLabel(data, base) {
    const fromPayload = pickAwardLabel(data.award) ||
        pickAwardLabel(data.award_info)

    if (fromPayload) {
        return fromPayload
    }

    const certAwards = filterCertificateAwards((base && base.awards) || [])
    if (certAwards.length) {
        return pickAwardLabel(certAwards[0])
    }

    const awards = (base && base.awards) || []
    if (awards.length) {
        return pickAwardLabel(awards[0])
    }

    return 'Not Awarded'
}

export function resolvePortfolioIdeaTrack(data, base) {
    const projectInfo = (base && base.projectInfo) || {}
    const application = (base && base.application) || {}

    return data.idea_track ||
        data.track_name ||
        projectInfo.project_product ||
        projectInfo.track_name ||
        application.application_project ||
        projectInfo.project_ename ||
        ''
}

function firstNonEmpty() {
    for (let i = 0; i < arguments.length; i++) {
        const value = arguments[i]
        if (value == null) {
            continue
        }
        if (Array.isArray(value) && value.length) {
            return value
        }
        if (!Array.isArray(value) && String(value).trim()) {
            return value
        }
    }
    return ''
}

function normalizeActionUrl(value) {
    const raw = String(value || '').trim()
    if (!raw) {
        return ''
    }
    if (/^#/.test(raw)) {
        return raw
    }
    return safePublicUrl(resolveDisplayMediaUrl(raw))
}

export function resolvePortfolioProjectCycle(data, base) {
    const projectManage = (base && base.projectManage) || {}
    const projectInfo = (base && base.projectInfo) || {}
    const entryDate = (projectManage && projectManage.entry_date) || []

    return data.project_cycle ||
        data.cycle_name ||
        data.application_cycle ||
        resolveProjectCycleLabel(entryDate, base && base.submissionDate) ||
        projectInfo.project_year ||
        ''
}

export function normalizePortfolioPayload(payload, fallback) {
    const data = payload || {}
    const base = fallback || {}

    let introductionRaw = data.introduction || data.project_introduction || ''
    let approachRaw = data.approach || ''
    let highlightsRaw = firstNonEmpty(
        data.impact_recognition,
        data.impact_and_recognition,
        data.highlights_impacts,
        data.highlights,
        [data.actual_effects_impact, data.innovation_uniqueness, data.social_value]
            .filter(Boolean)
            .join('\n\n')
    )

    // If backend still returns raw Project Summary only, derive the three sections locally.
    const needsLocalNarrative = (!approachRaw || !highlightsRaw) && introductionRaw &&
        (String(introductionRaw).replace(/<[^>]+>/g, ' ').trim().split(/\s+/).length > 220)
    if (needsLocalNarrative) {
        const derived = deriveNarrativesFromSummary(
            stripToPlain(introductionRaw),
            data.project_title || (base.projectInfo && base.projectInfo.project_title) || ''
        )
        if (!approachRaw) {
            approachRaw = derived.approach
        }
        if (!highlightsRaw) {
            highlightsRaw = derived.highlights
        }
        // Replace oversized raw dump with a controlled introduction.
        if (String(introductionRaw).replace(/<[^>]+>/g, ' ').trim().split(/\s+/).length > 220) {
            introductionRaw = derived.introduction
        }
    }

    return {
        projectTitle: data.project_title ||
            (base.projectInfo && base.projectInfo.project_title) ||
            (base.application && base.application.event) ||
            '',
        projectCycle: resolvePortfolioProjectCycle(data, base),
        ideaTrack: resolvePortfolioIdeaTrack(data, base),
        innovationName: data.innovation_name ||
            resolveInnovationName(
                data.track_code ||
                data.idea_track ||
                data.track_name ||
                (base.projectInfo && (base.projectInfo.project_code || base.projectInfo.project_product)) ||
                ''
            ),
        awardLabel: resolvePortfolioAwardLabel(data, base),
        members: normalizeMembers(
            data.members ||
            data.team_members ||
            base.members ||
            []
        ),
        summaryVideoUrl: resolveDisplayMediaUrl(
            data.summary_video_url ||
            data.video_url ||
            base.summaryVideoUrl ||
            ''
        ),
        introduction: narrativeToHtml(introductionRaw),
        approach: narrativeToHtml(approachRaw),
        keyOutcomes: '',
        ideaBoardImages: normalizeIdeaBoardImages(
            data.ideaboard_images || data.idea_board_images || data.deliverable_images || []
        ),
        highlightsImpacts: narrativeToHtml(highlightsRaw),
        ethicsSummary: narrativeToHtml(data.ethics_summary || data.ethics_statement || ''),
        ethicsUrl: normalizeActionUrl(data.ethics_url || data.ethics_statement_url || ''),
        reflectionTabs: normalizeReflectionTabs(data.reflections || data.reflection_tabs || {
            professional_learning: data.professional_learning_reflection,
            management_teamwork: data.management_teamwork_reflection,
            weakness_future: data.weakness_future_improvement
        }),
        generationStatus: data.generation_status || data.status ||
            ((introductionRaw || approachRaw || highlightsRaw) ? 'ready' : 'pending'),
        generationMode: data.generation_mode || (needsLocalNarrative ? 'frontend-fallback' : '')
    }
}

export function normalizeReflectionTabs(raw) {
    const data = raw || {}
    const rows = Array.isArray(data)
        ? data
        : [
            {
                key: 'professional_learning',
                label: 'Professional Learning',
                content: data.professional_learning || data.professional_learning_reflection || data.professionalLearning || ''
            },
            {
                key: 'management_teamwork',
                label: 'Management & Teamwork',
                content: data.management_teamwork || data.management_teamwork_reflection || data.teamwork || ''
            },
            {
                key: 'weakness_future',
                label: 'Weakness & Future Improvement',
                content: data.weakness_future || data.weakness_future_improvement || data.future_improvement || ''
            }
        ]

    const hasContent = rows.some(row => row.content || row.body || row.text)
    if (!hasContent) return []
    return rows.map(row => ({
        key: row.key || row.id || row.label,
        label: row.label || row.title || '',
        html: narrativeToHtml(row.content || row.body || row.text || '')
    })).filter(row => row.label)
}

function stripToPlain(value) {
    return String(value || '')
        .replace(/<\s*br\s*\/?\s*>/gi, ' ')
        .replace(/<\/\s*p\s*>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
}

function clipWords(text, maxWords) {
    const words = String(text || '').trim().split(/\s+/).filter(Boolean)
    if (words.length <= maxWords) {
        return words.join(' ')
    }
    return words.slice(0, maxWords).join(' ').replace(/[.,;:\s]+$/, '') + '.'
}

function deriveNarrativesFromSummary(summaryText, projectTitle) {
    const plain = String(summaryText || '').trim()
    const title = projectTitle || 'this project'
    const sentences = plain
        .replace(/([.!?。！？])\s+/g, '$1\n')
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean)
    const take = (start, count) => sentences.slice(start, start + count).join(' ')

    let introduction = take(0, 5)
    if (!introduction) {
        introduction = 'This portfolio presents ' + title +
            ', a Next Idea Matters team project grounded in community needs, careful research, and practical implementation.'
    }

    let approach = take(5, 5)
    if (!approach) {
        approach = 'The team approached ' + title +
            ' through structured investigation, stakeholder engagement, iterative prototyping, and evidence-based refinement across the project cycle.'
    }

    let highlights = take(10, 5)
    if (!highlights) {
        highlights = 'Key impacts of ' + title +
            ' include clearer problem framing, actionable deliverables for the target community, and transferable competencies developed through collaborative project practice.'
    }

    return {
        introduction: clipWords(introduction, 180),
        approach: clipWords(approach, 180),
        highlights: clipWords(highlights, 180)
    }
}
